import jsPDF from 'jspdf';
import { SceneWithUserNames } from '../../types/scene.types';
import { BreakdownInfo } from '../../pages/Home/types';
import { ElementDataCache, ElementRelationDataCache, BreakdownDataCache } from '../../types/cache.types';
import { CalDateInfo, SlotInfo, ScheduleInfo } from '../../types/schedule.types';
import type { ExportScheduleCalendarParams } from './Templates/PDF-ScheduleCalendar';
import {
  DEFAULT_PDF_SCENES_EXPORT_SORT,
  type PdfScenesExportSort
} from './exportSceneSort';
import { buildExportDownloadFileName } from './exportFileName';

export type { PdfExportSortField, PdfExportSortOrder, PdfScenesExportSort } from './exportSceneSort';
export { DEFAULT_PDF_SCENES_EXPORT_SORT } from './exportSceneSort';

// 语言类型定义
export type SupportedLanguage = 'zh-CN' | 'zh-HK' | 'en';

// 字体文件映射
const FONT_FILES = {
  'zh-CN': {
    normal: '/fonts/AlibabaPuHuiTi-2-45-Light.ttf',
    bold: '/fonts/AlibabaPuHuiTi-2-85-Bold.ttf',
    name: 'AlibabaPuHuiTi'
  },
  'zh-HK': {
    normal: '/fonts/AlibabaPuHuiTi-2-45-Light.ttf', // 暂时使用简体字体
    bold: '/fonts/AlibabaPuHuiTi-2-85-Bold.ttf',
    name: 'AlibabaPuHuiTi'
  },
  // 与界面语言无关：用户数据（片名、场次摘要等）常含中文，英文导出仅影响 i18n 文案，字体仍需 CJK
  'en': {
    normal: '/fonts/AlibabaPuHuiTi-2-45-Light.ttf',
    bold: '/fonts/AlibabaPuHuiTi-2-85-Bold.ttf',
    name: 'AlibabaPuHuiTi'
  }
};



// 将ArrayBuffer转换为Base64
const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// 注册字体
const registerFont = async (doc: jsPDF, language: SupportedLanguage) => {
  const fontConfig = FONT_FILES[language];
  // 如果语言不在支持列表中，使用默认字体
  if (!fontConfig) {
    console.warn(`不支持的语言: ${language}，使用默认字体`);
    return true;
  }
  if (!fontConfig.normal && !fontConfig.bold) {
    // 英文或其他使用默认字体的语言，无需注册
    return true;
  }
  
  try {
    // 加载普通字体
    if (fontConfig.normal) {
      console.log(`开始加载字体: ${fontConfig.normal}`);
      const normalFontResponse = await fetch(fontConfig.normal);
      if (!normalFontResponse.ok) {
        throw new Error(`字体文件加载失败: ${fontConfig.normal}, 状态码: ${normalFontResponse.status}`);
      }
      const normalFontArrayBuffer = await normalFontResponse.arrayBuffer();
      const normalFontBase64 = arrayBufferToBase64(normalFontArrayBuffer);
      doc.addFileToVFS(`${fontConfig.name}-normal.ttf`, normalFontBase64);
      doc.addFont(`${fontConfig.name}-normal.ttf`, fontConfig.name, 'normal');
      console.log(`字体 ${fontConfig.name} (normal) 注册成功`);
    }
    
    // 加载粗体
    if (fontConfig.bold) {
      console.log(`开始加载字体: ${fontConfig.bold}`);
      const boldFontResponse = await fetch(fontConfig.bold);
      if (!boldFontResponse.ok) {
        throw new Error(`字体文件加载失败: ${fontConfig.bold}, 状态码: ${boldFontResponse.status}`);
      }
      const boldFontArrayBuffer = await boldFontResponse.arrayBuffer();
      const boldFontBase64 = arrayBufferToBase64(boldFontArrayBuffer);
      doc.addFileToVFS(`${fontConfig.name}-bold.ttf`, boldFontBase64);
      doc.addFont(`${fontConfig.name}-bold.ttf`, fontConfig.name, 'bold');
      console.log(`字体 ${fontConfig.name} (bold) 注册成功`);
    }
    
    return true;
  } catch (error) {
    console.error(`${language}字体注册失败:`, error);
    return false;
  }
};

// 设置字体
const setFont = (doc: jsPDF, language: SupportedLanguage, style: 'normal' | 'bold' = 'normal') => {
  const fontConfig = FONT_FILES[language];
  // 如果语言不在支持列表中，使用默认字体
  if (!fontConfig || !fontConfig.name) {
    // 使用默认字体
    doc.setFont('helvetica', style);
  } else {
    doc.setFont(fontConfig.name, style);
  }
};

/**
 * 以可控时序触发 PDF 下载，并在「序列化 + 下载触发」阶段反馈进度。
 * 说明：浏览器不提供“文件落盘完成”事件，因此 100 表示已触发下载。
 */
async function savePdfWithProgress(
  doc: jsPDF,
  fileName: string,
  onProgress?: (progress: number) => void,
  range: { start: number; end: number } = { start: 92, end: 100 }
): Promise<void> {
  onProgress?.(range.start);
  await new Promise((resolve) => setTimeout(resolve, 0));

  const pdfBytes = doc.output('arraybuffer') as ArrayBuffer;
  onProgress?.(Math.max(range.start, range.end - 2));

  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  onProgress?.(range.end);
}

// 导出函数，供外部调用
export const exportPDF = (
  scenes: SceneWithUserNames[],
  breakdownInfo: BreakdownInfo,
  template: 'breakdown-a4' | 'sets-a4' | 'breakdown-a3' | 'sets-a3' | 'breakdowndetail-a4',
  language: SupportedLanguage = 'zh-CN',
  secondaryLanguage?: SupportedLanguage,
  optionName?: string,
  breakdownId?: string,
  getElementCache?: (breakdownId: string) => ElementDataCache | null,
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null,
  onProgress?: (progress: number) => void,
  scenesExportSort?: PdfScenesExportSort
) => {
  const sortOpt = scenesExportSort ?? DEFAULT_PDF_SCENES_EXPORT_SORT;
  // 动态导入模板函数
  const importTemplate = async () => {
    switch (template) {
      case 'breakdown-a4':
      case 'sets-a4':
        const moduleA4 = await import('./Templates/PDF-Breakdown-A4');
        return { func: moduleA4.exportScenesTableA4, kind: 'a4' as const };
      case 'breakdown-a3':
        const moduleA3 = await import('./Templates/PDF-Breakdown-A3');
        return { func: moduleA3.exportScenesTableA3, kind: 'breakdown-a3' as const };
      case 'sets-a3':
        const moduleSetsA3 = await import('./Templates/PDF-Breakdown-A3');
        return { func: moduleSetsA3.exportSetsTableA3, kind: 'sets-a3' as const };
      case 'breakdowndetail-a4':
        const moduleBreakdownDetailA4 = await import('./Templates/PDF-BreakdownDetail-A4');
        return { func: moduleBreakdownDetailA4.exportScenesDetailA4, kind: 'detail-a4' as const };
      default:
        console.error('未知的模板类型:', template);
        return null;
    }
  };

  // 异步导出
  const performExport = async () => {
    try {
      onProgress?.(10); // 开始导出
      
      const templateResult = await importTemplate();
      if (!templateResult) {
        onProgress?.(0);
        return;
      }
      
      onProgress?.(30); // 模板加载完成
      
      let doc = new jsPDF();
      // 注册所有需要的字体
      await registerFont(doc, language);
      
      onProgress?.(50); // 字体注册完成
      
      if (templateResult.kind === 'detail-a4') {
        doc = await (templateResult.func as any)(
          doc,
          scenes,
          breakdownInfo,
          language,
          secondaryLanguage,
          'sceneCode',
          optionName,
          breakdownId,
          getElementCache,
          getElementRelationCache
        );
      } else if (templateResult.kind === 'a4') {
        doc = await (templateResult.func as any)(
          doc,
          scenes,
          breakdownInfo,
          language,
          secondaryLanguage,
          sortOpt,
          optionName,
          breakdownId,
          getElementCache,
          getElementRelationCache
        );
      } else {
        doc = await (templateResult.func as any)(
          doc,
          scenes,
          breakdownInfo,
          language,
          secondaryLanguage,
          sortOpt,
          optionName,
          breakdownId,
          getElementCache,
          getElementRelationCache
        );
      }
      
      onProgress?.(90); // PDF生成完成
      
      const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
      const optionFallbackKey =
        template === 'sets-a4' || template === 'sets-a3'
          ? 'export.setsTable'
          : template === 'breakdowndetail-a4'
            ? 'export.breakdownDetail'
            : 'export.breakdownTable';
      const fileName = buildExportDownloadFileName({
        lng: language,
        filmName,
        optionDisplayName: optionName,
        optionFallbackI18nKey: optionFallbackKey,
        breakdownDisplayName: breakdownInfo.breakdownName,
        extension: 'pdf'
      });
      await savePdfWithProgress(doc, fileName, onProgress, { start: 92, end: 100 });
    } catch (error) {
      console.error('导出过程中出错:', error);
      onProgress?.(0);
      throw error;
    }
  };

  return performExport();
};

export type ExportScheduleCalendarPdfInput = Omit<ExportScheduleCalendarParams, 'onProgress'> & {
  secondaryLanguage?: SupportedLanguage;
  onProgress?: (progress: number) => void;
};

/** 制作日历 PDF：独立入口，避免与分场表 exportPDF 参数混杂 */
export const exportScheduleCalendarPdf = (input: ExportScheduleCalendarPdfInput) => {
  const perform = async () => {
    try {
      input.onProgress?.(8);
      const { exportScheduleCalendarDocument } = await import('./Templates/PDF-ScheduleCalendar');
      const doc = await exportScheduleCalendarDocument(new jsPDF(), {
        ...input,
        onProgress: (p) => input.onProgress?.(p)
      });
      input.onProgress?.(90);
      const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
      const fileName = buildExportDownloadFileName({
        lng: input.language,
        filmName,
        optionDisplayName: input.optionName,
        optionFallbackI18nKey: 'export.shootingCalendarTable',
        breakdownDisplayName: input.scheduleInfo?.scheduleName,
        breakdownFallbackI18nKey: 'export.schedule',
        extension: 'pdf'
      });
      await savePdfWithProgress(doc, fileName, input.onProgress, { start: 92, end: 100 });
    } catch (error) {
      console.error('制作日历导出失败:', error);
      input.onProgress?.(0);
      throw error;
    }
  };
  return perform();
};

export type ExportScheduleDetailPdfInput = {
  language: SupportedLanguage;
  secondaryLanguage?: SupportedLanguage;
  scenes: SceneWithUserNames[];
  breakdownInfo: BreakdownInfo;
  breakdownId?: string;
  scheduleInfo: ScheduleInfo | null;
  scheduleSolts: SlotInfo[];
  calendarDays: CalDateInfo[];
  exportRange?: { start: string; end: string };
  optionName?: string;
  getElementCache?: (breakdownId: string) => ElementDataCache | null;
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null;
  onProgress?: (progress: number) => void;
};

/** 拍摄计划详情 PDF：独立入口 */
export const exportScheduleDetailPdf = (input: ExportScheduleDetailPdfInput) => {
  const perform = async () => {
    try {
      input.onProgress?.(8);
      const { exportScheduleDetailA4Document } = await import('./Templates/PDF-ScheduleDetail-A4');
      const doc = await exportScheduleDetailA4Document(new jsPDF(), {
        language: input.language,
        secondaryLanguage: input.secondaryLanguage,
        scenes: input.scenes,
        breakdownInfo: input.breakdownInfo,
        scheduleInfo: input.scheduleInfo,
        scheduleSolts: input.scheduleSolts,
        calendarDays: input.calendarDays,
        exportRange: input.exportRange,
        optionName: input.optionName,
        breakdownId: input.breakdownId,
        getElementCache: input.getElementCache,
        getElementRelationCache: input.getElementRelationCache,
        onProgress: input.onProgress
      });
      input.onProgress?.(90);
      const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
      const fileName = buildExportDownloadFileName({
        lng: input.language,
        filmName,
        optionDisplayName: input.optionName,
        optionFallbackI18nKey: 'export.shootScheduleDetail',
        breakdownDisplayName: input.scheduleInfo?.scheduleName,
        breakdownFallbackI18nKey: 'export.schedule',
        extension: 'pdf'
      });
      await savePdfWithProgress(doc, fileName, input.onProgress, { start: 92, end: 100 });
    } catch (error) {
      console.error('拍摄计划详情导出失败:', error);
      input.onProgress?.(0);
      throw error;
    }
  };
  return perform();
};

export type ExportOneLineSchedulePdfInput = {
  language: SupportedLanguage;
  secondaryLanguage?: SupportedLanguage;
  scenes: SceneWithUserNames[];
  breakdownInfo: BreakdownInfo;
  breakdownId?: string;
  scheduleInfo: ScheduleInfo | null;
  scheduleSolts: SlotInfo[];
  calendarDays: CalDateInfo[];
  exportRange?: { start: string; end: string };
  optionName?: string;
  getElementCache?: (breakdownId: string) => ElementDataCache | null;
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null;
  onProgress?: (progress: number) => void;
};

/** 简约拍摄计划 PDF：独立入口 */
export const exportOneLineSchedulePdf = (input: ExportOneLineSchedulePdfInput) => {
  const perform = async () => {
    try {
      input.onProgress?.(8);
      const { exportOneLineScheduleA4Document } = await import('./Templates/PDF-One-lineSchedule-A4');
      const doc = await exportOneLineScheduleA4Document(new jsPDF(), {
        language: input.language,
        secondaryLanguage: input.secondaryLanguage,
        scenes: input.scenes,
        breakdownInfo: input.breakdownInfo,
        scheduleInfo: input.scheduleInfo,
        scheduleSolts: input.scheduleSolts,
        calendarDays: input.calendarDays,
        exportRange: input.exportRange,
        optionName: input.optionName,
        breakdownId: input.breakdownId,
        getElementCache: input.getElementCache,
        getElementRelationCache: input.getElementRelationCache,
        onProgress: input.onProgress
      });
      input.onProgress?.(90);
      const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
      const fileName = buildExportDownloadFileName({
        lng: input.language,
        filmName,
        optionDisplayName: input.optionName,
        optionFallbackI18nKey: 'export.oneLineSchedule',
        breakdownDisplayName: input.scheduleInfo?.scheduleName,
        breakdownFallbackI18nKey: 'export.schedule',
        extension: 'pdf'
      });
      await savePdfWithProgress(doc, fileName, input.onProgress, { start: 92, end: 100 });
    } catch (error) {
      console.error('简约拍摄计划导出失败:', error);
      input.onProgress?.(0);
      throw error;
    }
  };
  return perform();
};

export type ExportCastBreakdownPdfInput = {
  language: SupportedLanguage;
  secondaryLanguage?: SupportedLanguage;
  breakdownInfo: BreakdownInfo;
  breakdownId: string;
  optionName?: string;
  getElementCache?: (breakdownId: string) => ElementDataCache | null;
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null;
  getBreakdownCache?: (breakdownId: string) => BreakdownDataCache | null;
  onProgress?: (progress: number) => void;
};

/** 主要角色场次清单 PDF（A4 竖版） */
export const exportCastBreakdownPdf = (input: ExportCastBreakdownPdfInput) => {
  const perform = async () => {
    try {
      input.onProgress?.(8);
      const { exportCastBreakdownA4Document } = await import('./Templates/PDF-CastBreakdown-A4');
      const doc = await exportCastBreakdownA4Document(new jsPDF(), {
        language: input.language,
        secondaryLanguage: input.secondaryLanguage,
        breakdownInfo: input.breakdownInfo,
        breakdownId: input.breakdownId,
        getElementCache: input.getElementCache,
        getElementRelationCache: input.getElementRelationCache,
        getBreakdownCache: input.getBreakdownCache,
        optionName: input.optionName,
        onProgress: input.onProgress
      });
      input.onProgress?.(90);
      const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
      const fileName = buildExportDownloadFileName({
        lng: input.language,
        filmName,
        optionDisplayName: input.optionName,
        optionFallbackI18nKey: 'export.castSceneList',
        breakdownDisplayName: input.breakdownInfo.breakdownName,
        breakdownFallbackI18nKey: 'export.untitledBreakdown',
        stripBreakdownSegmentWhitespace: true,
        extension: 'pdf'
      });
      await savePdfWithProgress(doc, fileName, input.onProgress, { start: 92, end: 100 });
    } catch (error) {
      console.error('主要角色角色分场导出失败:', error);
      input.onProgress?.(0);
      throw error;
    }
  };
  return perform();
};

// 导出字体设置函数，供模板使用
export { registerFont, setFont };
